<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import { getContratoRecibos } from 'dashboard/api/supra';

const props = defineProps({
  numeroContrato: {
    type: String,
    required: true,
  },
  explotacion: {
    type: String,
    required: true,
  },
});

useI18n();

// State
const facturas = ref([]);
const isLoading = ref(true);
const error = ref(null);
const downloadingPdf = ref(null);

// Methods
const fetchFacturas = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const res = await getContratoRecibos(props.numeroContrato, props.explotacion);
    const recibos = res.data?.data?.recibos ?? [];

    facturas.value = recibos.map(r => ({
      numero: r.numFactura,
      numeroFactura: r.numFactura,
      numFactura: r.numFactura,
      fechaEmision: r.fechaVencimiento,
      importeTotal: r.importeTotal,
      estado: r.estadoTexto === 'pagado' ? 'PAGADA' : r.estadoTexto.toUpperCase(),
      downloadUrl: r.downloadUrl,
      periodo: r.periodo,
      fechaPago: r.fechaPago,
      metodoPago: r.metodoPago,
      folioPago: r.folioPago,
    }));
  } catch (err) {
    error.value = err.message || 'Error al cargar las facturas';
    facturas.value = [];
  } finally {
    isLoading.value = false;
  }
};

const downloadPdf = async factura => {
  if (!factura.downloadUrl) {
    useAlert('No se puede descargar: URL no disponible');
    return;
  }

  const numFactura = factura.numero || factura.numeroFactura || factura.numFactura;
  downloadingPdf.value = numFactura;

  try {
    // URLs relativas (/api/...) requieren auth → fetch como blob con axios
    if (factura.downloadUrl.startsWith('/')) {
      const response = await axios.get(factura.downloadUrl, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `factura_${numFactura}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } else {
      // URL directa (SUPRA)
      window.open(factura.downloadUrl, '_blank');
    }
  } catch {
    useAlert('Error al descargar el PDF');
  } finally {
    downloadingPdf.value = null;
  }
};

const getEstadoColor = estado => {
  if (!estado) return 'slate';
  if (estado === '3' || estado === 3) return 'green';
  if (estado === '4' || estado === 4) return 'amber';
  const estadoUpper = String(estado).toUpperCase();
  if (estadoUpper === 'COBRADA' || estadoUpper === 'PAGADA') return 'green';
  if (estadoUpper === 'PENDIENTE') return 'amber';
  if (estadoUpper === 'VENCIDA' || estadoUpper === 'IMPAGADA') return 'ruby';
  return 'slate';
};

const formatCurrency = value => {
  const num = parseFloat(value || 0);
  return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

onMounted(() => {
  fetchFacturas();
});
</script>

<template>
  <div class="space-y-4">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <p class="text-sm text-n-slate-11 mt-2">
          {{ $t('CONTRACTS.FACTURAS.LOADING') }}
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center py-12 gap-4"
    >
      <span class="i-lucide-alert-circle text-6xl text-n-ruby-11" />
      <p class="text-n-slate-11">{{ error }}</p>
      <Button color="blue" size="small" @click="fetchFacturas">
        {{ $t('CONTRACTS.FACTURAS.RETRY') }}
      </Button>
    </div>

    <!-- Facturas List -->
    <div v-else-if="facturas.length > 0" class="space-y-4">
      <!-- Summary Card -->
      <div class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3
          class="text-lg font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
        >
          <span class="i-lucide-file-text" />
          {{ $t('CONTRACTS.FACTURAS.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('CONTRACTS.FACTURAS.FOUND_COUNT', { count: facturas.length }) }}
        </p>
      </div>

      <!-- Facturas Table -->
      <div
        class="bg-n-background border border-n-weak rounded-lg overflow-hidden"
      >
        <table class="w-full">
          <thead class="bg-n-solid-3 border-b border-n-weak">
            <tr>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ $t('CONTRACTS.FACTURAS.INVOICE_NUMBER') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ $t('CONTRACTS.FACTURAS.DATE') }}
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ $t('CONTRACTS.FACTURAS.AMOUNT') }}
              </th>
              <th
                class="px-4 py-3 text-center text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ $t('CONTRACTS.FACTURAS.STATUS') }}
              </th>
              <th
                class="px-4 py-3 text-center text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ $t('CONTRACTS.FACTURAS.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="(factura, index) in facturas"
              :key="factura.numero || index"
              class="hover:bg-n-alpha-1 transition-colors"
            >
              <td class="px-4 py-3">
                <span class="font-mono text-sm text-n-slate-12">
                  {{ factura.numero || '-' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-12">
                {{ formatDate(factura.fechaEmision) }}
              </td>
              <td class="px-4 py-3 text-right">
                <span class="font-semibold text-n-slate-12">
                  {{ formatCurrency(factura.importeTotal) }}
                </span>
              </td>
              <td class="px-4 py-3 text-center">
                <Badge
                  v-if="factura.estado"
                  :color="getEstadoColor(factura.estado)"
                  size="small"
                >
                  {{
                    factura.estado === '3' || factura.estado === 3
                      ? $t('CONTRACTS.FACTURAS.PAID')
                      : factura.estado === '4' || factura.estado === 4
                        ? $t('CONTRACTS.FACTURAS.PENDING')
                        : factura.estado
                  }}
                </Badge>
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                <span v-else class="text-n-slate-10">-</span>
              </td>
              <td class="px-4 py-3 text-center">
                <Button
                  variant="ghost"
                  size="small"
                  :disabled="downloadingPdf === factura.numero"
                  @click="downloadPdf(factura)"
                >
                  <Spinner
                    v-if="downloadingPdf === factura.numero"
                    :size="16"
                  />
                  <span v-else class="i-lucide-download" />
                  {{ $t('CONTRACTS.FACTURAS.PDF') }}
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex flex-col items-center justify-center py-12 gap-4">
      <span class="i-lucide-file-x text-6xl text-n-slate-10" />
      <p class="text-n-slate-11">
        {{ $t('CONTRACTS.FACTURAS.NOT_FOUND') }}
      </p>
      <Button color="slate" size="small" @click="fetchFacturas">
        <span class="i-lucide-refresh-cw mr-2" />
        {{ $t('CONTRACTS.FACTURAS.REFRESH') }}
      </Button>
    </div>
  </div>
</template>
